/**
 * Email Design Entity Schemas
 *
 * Email Broadcast Builder domain — the broadcast email tool authors with
 * Puck and bakes to email-safe HTML via react-email. The Puck `Data`
 * produced by the email builder is persisted on the broadcast's
 * `metadata.design` and is the source of truth a save → reload round-trip
 * must preserve.
 *
 * Mirrors `page.ts` exactly (the keystone): strict on what we own
 * (component types + their props) but permissive on Puck-owned metadata
 * (`id`, `readOnly`, etc.) via `.passthrough()` on every block node AND the
 * root, plus a top-level `zones` map.
 *
 * **Why the `zones` map is load-bearing:** Puck stores the children of
 * DropZone-based blocks (the two-column row) FLAT in `Data.zones`, keyed
 * `<componentId>:col-<i>` — NOT nested inside the parent block's props. A
 * strict union without passthrough + a `zones` map would silently strip
 * the Puck-owned keys and drop every column child on save. The
 * `email-design` round-trip test guards exactly this.
 *
 * Block discriminator values are PascalCase and match the Puck component
 * keys in `apps/web/.../email-builder/email-config.tsx`, so the Puck `Data`
 * validates against `EmailDesignSchema` with no translation layer. Exported
 * prop schemas/types/constants are `Email`-prefixed to avoid colliding with
 * the custom-pages `page.ts` exports (both flow through `entities/index`).
 */

import { z } from 'zod'

// Hex color regex — full 6-digit form only (mirrors page.ts).
const HEX_COLOR_RE = /^#[0-9a-fA-F]{6}$/

export const EMAIL_ALIGN_VALUES = ['left', 'center', 'right'] as const
export const EmailAlignSchema = z.enum(EMAIL_ALIGN_VALUES)
export type EmailAlign = z.infer<typeof EmailAlignSchema>

// Email image/link URLs are https-only — email clients block mixed content
// and the bake layer (U2) re-validates against localhost / private IPs.
const httpsUrl = z
  .string()
  .url()
  .refine(u => /^https:\/\//i.test(u), { message: 'URL must use the https: scheme' })

// ============================================
// Email block types
// ============================================

// Upper bound on how many blocks a single email design may carry — at the
// top level (`content`) and within each two-column zone. A hard cap keeps a
// hostile or runaway client from persisting an unbounded design that the bake
// + send path would then have to render. ~100 blocks is far beyond any real
// newsletter; it exists to fail closed, not to constrain authoring.
export const EMAIL_MAX_BLOCKS = 100

export const EMAIL_BLOCK_TYPES = [
  'Header',
  'Heading',
  'RichText',
  'Image',
  'Button',
  'Divider',
  'Spacer',
  'TwoColumnRow',
  'Footer'
] as const
export const EmailBlockTypeSchema = z.enum(EMAIL_BLOCK_TYPES)
export type EmailBlockType = z.infer<typeof EmailBlockTypeSchema>

// Header — logo + optional banner image. Both URLs are optional so a
// half-built header (or a text-only/banner-only header) is still a valid
// design; the bake layer renders only what is present.
export const EmailHeaderPropsSchema = z
  .object({
    logoUrl: httpsUrl.optional(),
    logoAlt: z.string().max(500).optional(),
    logoWidth: z.number().int().positive().max(600).optional(),
    bannerUrl: httpsUrl.optional(),
    bannerAlt: z.string().max(500).optional(),
    backgroundColor: z.string().regex(HEX_COLOR_RE, 'backgroundColor must be a 6-digit hex value').optional(),
    align: EmailAlignSchema.optional()
  })
  .passthrough()
export type EmailHeaderProps = z.infer<typeof EmailHeaderPropsSchema>

export const EmailHeadingPropsSchema = z
  .object({
    text: z.string().min(1).max(200),
    level: z.union([z.literal(1), z.literal(2), z.literal(3)]).default(2),
    align: EmailAlignSchema.optional(),
    color: z.string().regex(HEX_COLOR_RE, 'color must be a 6-digit hex value').optional()
  })
  .passthrough()
export type EmailHeadingProps = z.infer<typeof EmailHeadingPropsSchema>

// Rich text — email-safe inline markup (bold / italic / link only) plus the
// `{{school_name}}` token. The authoring field sanitizes to this constrained
// set; the schema only bounds size. `<style>` / raw `<div>` never reach here.
export const EmailRichTextPropsSchema = z
  .object({
    html: z.string().max(50_000)
  })
  .passthrough()
export type EmailRichTextProps = z.infer<typeof EmailRichTextPropsSchema>

export const EmailImagePropsSchema = z
  .object({
    url: httpsUrl,
    alt: z.string().max(500).default(''),
    caption: z.string().max(500).optional(),
    align: EmailAlignSchema.optional(),
    // Optional click-through target for the image (e.g. a hero linking to the
    // school site). https-only like every other email link.
    href: httpsUrl.optional(),
    // Intrinsic width hint in px; the bake layer clamps to the content width.
    width: z.number().int().positive().max(1200).optional()
  })
  .passthrough()
export type EmailImageProps = z.infer<typeof EmailImagePropsSchema>

export const EmailButtonPropsSchema = z
  .object({
    text: z.string().min(1).max(120),
    href: httpsUrl,
    backgroundColor: z.string().regex(HEX_COLOR_RE, 'backgroundColor must be a 6-digit hex value').optional(),
    textColor: z.string().regex(HEX_COLOR_RE, 'textColor must be a 6-digit hex value').optional(),
    align: EmailAlignSchema.optional()
  })
  .passthrough()
export type EmailButtonProps = z.infer<typeof EmailButtonPropsSchema>

export const EMAIL_DIVIDER_THICKNESS_MIN = 1
export const EMAIL_DIVIDER_THICKNESS_MAX = 16
export const EMAIL_DIVIDER_THICKNESS_DEFAULT = 2

export const EmailDividerPropsSchema = z
  .object({
    color: z.string().regex(HEX_COLOR_RE, 'color must be a 6-digit hex value').optional(),
    thickness: z
      .number()
      .int()
      .min(EMAIL_DIVIDER_THICKNESS_MIN)
      .max(EMAIL_DIVIDER_THICKNESS_MAX)
      .default(EMAIL_DIVIDER_THICKNESS_DEFAULT)
  })
  .passthrough()
export type EmailDividerProps = z.infer<typeof EmailDividerPropsSchema>

export const EMAIL_SPACER_HEIGHT_MIN = 4
export const EMAIL_SPACER_HEIGHT_MAX = 200
export const EMAIL_SPACER_HEIGHT_DEFAULT = 16

export const EmailSpacerPropsSchema = z
  .object({
    height: z
      .number()
      .int()
      .min(EMAIL_SPACER_HEIGHT_MIN)
      .max(EMAIL_SPACER_HEIGHT_MAX)
      .default(EMAIL_SPACER_HEIGHT_DEFAULT)
  })
  .passthrough()
export type EmailSpacerProps = z.infer<typeof EmailSpacerPropsSchema>

// Two-column row — a layout block with two child zones (`col-0`, `col-1`).
// Children live in `Data.zones` keyed `<componentId>:col-<i>`, validated via
// the top-level `zones` map. The schema stays minimal because the children
// are not nested in props. `stackOnMobile` drives the bake layer's responsive
// media query (R10).
export const EmailTwoColumnRowPropsSchema = z
  .object({
    stackOnMobile: z.boolean().default(true),
    backgroundColor: z.string().regex(HEX_COLOR_RE, 'backgroundColor must be a 6-digit hex value').optional(),
    verticalAlign: z.enum(['top', 'middle', 'bottom']).optional()
  })
  .passthrough()
export type EmailTwoColumnRowProps = z.infer<typeof EmailTwoColumnRowPropsSchema>

// Footer — school info + a MANDATORY unsubscribe link. The unsubscribe link
// has no removal affordance in the editor (compliance); the schema carries
// only its label. The bake layer emits the OneSignal-managed unsubscribe URL.
export const EmailFooterPropsSchema = z
  .object({
    schoolName: z.string().max(200).optional(),
    address: z.string().max(500).optional(),
    contactEmail: z.string().email().optional(),
    unsubscribeLabel: z.string().min(1).max(120).default('Unsubscribe'),
    backgroundColor: z.string().regex(HEX_COLOR_RE, 'backgroundColor must be a 6-digit hex value').optional(),
    textColor: z.string().regex(HEX_COLOR_RE, 'textColor must be a 6-digit hex value').optional(),
    align: EmailAlignSchema.optional()
  })
  .passthrough()
export type EmailFooterProps = z.infer<typeof EmailFooterPropsSchema>

// ============================================
// EmailBlock discriminated union
// ============================================
//
// Top-level Puck-owned fields (id, readOnly, etc.) accepted via passthrough
// on every node — without this, Puck-owned keys are stripped on save.

export const EmailBlockSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('Header'), props: EmailHeaderPropsSchema }).passthrough(),
  z.object({ type: z.literal('Heading'), props: EmailHeadingPropsSchema }).passthrough(),
  z.object({ type: z.literal('RichText'), props: EmailRichTextPropsSchema }).passthrough(),
  z.object({ type: z.literal('Image'), props: EmailImagePropsSchema }).passthrough(),
  z.object({ type: z.literal('Button'), props: EmailButtonPropsSchema }).passthrough(),
  z.object({ type: z.literal('Divider'), props: EmailDividerPropsSchema }).passthrough(),
  z.object({ type: z.literal('Spacer'), props: EmailSpacerPropsSchema }).passthrough(),
  z.object({ type: z.literal('TwoColumnRow'), props: EmailTwoColumnRowPropsSchema }).passthrough(),
  z.object({ type: z.literal('Footer'), props: EmailFooterPropsSchema }).passthrough()
])
export type EmailBlock = z.infer<typeof EmailBlockSchema>

// ============================================
// EmailDesign (Puck Data wrapper)
// ============================================
//
// `content` is the top-level ordered block list; `zones` carries the flat
// DropZone children for two-column rows (keyed `<id>:col-<i>`); `root` carries
// page-level concerns (preheader, background). Every level passes through
// Puck-owned keys.

export const EmailDesignRootPropsSchema = z
  .object({
    preheader: z.string().max(500).optional(),
    backgroundColor: z.string().regex(HEX_COLOR_RE, 'backgroundColor must be a 6-digit hex value').optional(),
    contentBackgroundColor: z
      .string()
      .regex(HEX_COLOR_RE, 'contentBackgroundColor must be a 6-digit hex value')
      .optional()
  })
  .passthrough()
export type EmailDesignRootProps = z.infer<typeof EmailDesignRootPropsSchema>

export const EmailDesignSchema = z
  .object({
    content: z.array(EmailBlockSchema).max(EMAIL_MAX_BLOCKS),
    root: z
      .object({
        props: EmailDesignRootPropsSchema.optional()
      })
      .passthrough(),
    zones: z.record(z.string(), z.array(EmailBlockSchema).max(EMAIL_MAX_BLOCKS)).optional()
  })
  .passthrough()
export type EmailDesign = z.infer<typeof EmailDesignSchema>
